import React from 'react';
import { Button, DotLoading, Form, Input, NavBar } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { profileRebindPageStyles } from './styles';
import { CONTACT_PHONE_PATTERN, ProfileRebindFormValues, ProfileRebindState } from './types';

interface ProfileRebindViewProps {
  state: ProfileRebindState;
  onSubmit: (values: ProfileRebindFormValues) => Promise<void>;
}

export const ProfileRebindView: React.FC<ProfileRebindViewProps> = ({ state, onSubmit }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');

  if (state.loading) {
    return (
      <div className="webview-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <DotLoading />
      </div>
    );
  }

  return (
    <div className="webview-page">
      <NavBar onBack={() => navigate('/profile')}>{t('rebind.navTitle')}</NavBar>

      <div className="webview-section">
        <div style={profileRebindPageStyles.introCard}>
          <div style={profileRebindPageStyles.introTitle}>{t('rebind.title')}</div>
          <div style={profileRebindPageStyles.introText}>{t('rebind.subtitle')}</div>
        </div>
      </div>

      <div className="webview-section">
        <div style={profileRebindPageStyles.formCard}>
          <div className="webview-section-title">{t('rebind.formTitle')}</div>
          <div className="webview-subtitle" style={{ marginTop: 6 }}>
            {t('rebind.formSubtitle')}
          </div>

          <div style={profileRebindPageStyles.currentPhoneCard}>
            <div className="webview-caption">{t('rebind.currentPhoneLabel')}</div>
            <div style={{ marginTop: 8, fontSize: 18, fontWeight: 800, color: '#202437', wordBreak: 'break-all' }}>
              {state.initialPhone || t('notConfigured')}
            </div>
          </div>

          <Form
            layout="vertical"
            initialValues={{ contact_phone: state.initialPhone }}
            onFinish={async (values) => {
              await onSubmit(values as ProfileRebindFormValues);
            }}
            footer={
              <Button
                block
                type="submit"
                color="primary"
                loading={state.submitting}
                style={profileRebindPageStyles.submitButton}
              >
                {t('rebind.submit')}
              </Button>
            }
          >
            <Form.Item
              name="contact_phone"
              label={t('rebind.newPhoneLabel')}
              rules={[
                {
                  validator: (_, value) => {
                    const normalizedValue = typeof value === 'string' ? value.trim() : '';

                    if (!normalizedValue || CONTACT_PHONE_PATTERN.test(normalizedValue)) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error(t('rebind.phoneFormatRequired')));
                  },
                },
              ]}
            >
              <div style={profileRebindPageStyles.inputShell}>
                <Input
                  clearable
                  placeholder={t('rebind.phonePlaceholder')}
                  style={profileRebindPageStyles.input}
                />
              </div>
            </Form.Item>
          </Form>

          <div style={profileRebindPageStyles.hintCard}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#202437' }}>{t('rebind.noticeTitle')}</div>
            <div className="webview-micro" style={{ marginTop: 8, lineHeight: 1.7 }}>
              {t('rebind.noticeText')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
